import type { ReactNode } from "react";

export function Badge({ kind, children, title }: { kind: string; children: ReactNode; title?: string }) {
  return <span className={`badge ${kind}`} title={title}>{children}</span>;
}

export function StateDot({ state }: { state: string }) {
  return <span className={`dot-state ${state}`} />;
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children ? <p>{children}</p> : null}
      {action}
    </div>
  );
}

export function Spinner({ size = 13 }: { size?: number }) {
  return (
    <span
      style={{
        width: size, height: size, display: "inline-block", borderRadius: "50%",
        border: "2px solid var(--line-strong)", borderTopColor: "var(--accent)",
        animation: "pads-spin 700ms linear infinite",
      }}
    />
  );
}

export function ProgressBar({ value, state }: { value: number; state?: string }) {
  const color = state === "failed" ? "var(--bad)" : state === "cancelled" ? "var(--text-3)" : "var(--accent)";
  return (
    <div style={{ height: 4, background: "var(--bg-0)", borderRadius: 2, overflow: "hidden", flex: 1, minWidth: 40 }}>
      <div style={{ height: "100%", width: `${Math.round(value * 100)}%`, background: color, transition: "width 200ms" }} />
    </div>
  );
}

/** A control that is not built yet. Shows what it will be; produces no data. */
export function PlannedControl({ label, phase, title }: { label: string; phase?: number; title?: string }) {
  return (
    <button disabled className="ghost" title={title ?? `Planned for phase ${phase ?? "?"} — not implemented yet`}>
      {label}
      <span className="badge planned">Planned</span>
    </button>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label>{label}</label>
      {children}
      {hint ? <div className="tiny muted" style={{ marginTop: 3 }}>{hint}</div> : null}
    </div>
  );
}

export function KeyValue({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "5px 12px", alignItems: "baseline" }}>
      {rows.map(([k, v], i) => (
        <div key={i} style={{ display: "contents" }}>
          <div className="tiny muted" style={{ whiteSpace: "nowrap" }}>{k}</div>
          <div className="mono" style={{ overflowWrap: "anywhere" }}>{v}</div>
        </div>
      ))}
    </div>
  );
}
